import { LoggerLevel, metrics_category } from './type';
import monitor, { ExtraInfo, Monitor } from './monitor';

type LogFunc = (content: string, extra?: ExtraInfo) => void;

export interface ILogger {
  print: LogFunc;
  debug: LogFunc;
  info: LogFunc;
  warn: LogFunc;
  error: LogFunc;
  fatal: LogFunc;
}

const colorTable = {
  [LoggerLevel.print]: '#19be6b',
  [LoggerLevel.debug]: '#6db4f6',
  [LoggerLevel.info]: '#1890ff',
  [LoggerLevel.warn]: '#ff9900',
  [LoggerLevel.error]: '#ed4014',
  [LoggerLevel.fatal]: '#ff0000',
};

export class Logger implements ILogger {
  static level: LoggerLevel = LoggerLevel.none;

  private name: string | undefined;
  private monitor: Monitor;

  constructor(name?: string, monitorInstance: Monitor = monitor) {
    this.name = name;
    this.monitor = monitorInstance;
  }

  log(level: LoggerLevel, content: string, extra?: ExtraInfo) {
    this.monitor.emitLog(
      level,
      content,
      this.name
        ? {
            ...extra,
            [metrics_category]: this.name,
          }
        : extra,
    );
    if (level === LoggerLevel.none) return;

    if (Logger.level <= level) {
      let log = (...data: any[]) => {};
      switch (level) {
        case LoggerLevel.print:
          log = console.info.bind(console);
          break;
        case LoggerLevel.debug:
        case LoggerLevel.info:
          log = console.debug.bind(console);
          break;
        case LoggerLevel.warn:
          log = console.warn.bind(console);
          break;
        case LoggerLevel.error:
        case LoggerLevel.fatal:
          log = console.error.bind(console);
          break;
      }

      log?.(
        `%c%s`,
        `color: ${colorTable[level]}; font-weight: 700`,
        `[LINKCHAT_SDK]${this.name ? `-[${this.name}]` : ''}-[${
          LoggerLevel[level]
        }]:`,
        content,
        extra ?? '',
      );
    }
  }

  public print: LogFunc = (...args) => {
    this.log(LoggerLevel.print, ...args);
  };

  public debug: LogFunc = (...args) => {
    this.log(LoggerLevel.debug, ...args);
  };

  public info: LogFunc = (...args) => {
    this.log(LoggerLevel.info, ...args);
  };

  public warn: LogFunc = (...args) => {
    this.log(LoggerLevel.warn, ...args);
  };

  public error: LogFunc = (...args) => {
    this.log(LoggerLevel.error, ...args);
  };

  public fatal: LogFunc = (...args) => {
    this.log(LoggerLevel.fatal, ...args);
  };
}

export const setLoggerLevel = (level: LoggerLevel = LoggerLevel.none) => {
  Logger.level = level;
};

export default new Logger();
